import { Cookie, getCookie, setCookie } from '@lib/utils/cookies'
import { UtmParams } from '@grammarly/website-components'
import { extractUtmParams } from '@lib/utils/url'

export enum SocialEnum {
  Facebook = 'facebook.com',
  Twitter = 'x.com',
  Reddit = 'reddit.com',
  Youtube = 'youtube.com',
  Instagram = 'instagram.com',
  Linkedin = 'linkedin.com',
  LnkdIn = 'lnkd.in',
}

export enum OrganicEnum {
  Google = 'google.com',
  Yahoo = 'yahoo.com',
  Bing = 'bing.com',
}

const socialSourceMap: Record<string, string> = {
  [SocialEnum.Facebook]: 'facebook',
  [SocialEnum.Twitter]: 'twitter',
  [SocialEnum.Reddit]: 'reddit',
  [SocialEnum.Youtube]: 'youtube',
  [SocialEnum.Instagram]: 'instagram',
  [SocialEnum.Linkedin]: 'linkedin',
  [SocialEnum.LnkdIn]: 'linkedin',
}

const organicSourceMap: Record<string, string> = {
  [OrganicEnum.Google]: 'google',
  [OrganicEnum.Yahoo]: 'yahoo',
  [OrganicEnum.Bing]: 'bing',
}

const extractDomain = (urlString: string) => {
  try {
    const url = new URL(urlString)
    return url.hostname
  } catch (e) {
    return 'invalid-url'
  }
}

const isGrammarlyDomain = (url: string | undefined) => {
  return url?.includes('grammarly') || url?.includes(document.location.hostname) || false
}

export const getUtmParamsFromCookie = (): UtmParams | null => {
  const cookieUtmParams = getCookie(Cookie.utmParams)
  if (cookieUtmParams) {
    return JSON.parse(cookieUtmParams)
  }

  return null
}

const getUtmSourceAndUtmMedium = (): Partial<UtmParams> | undefined => {
  const documentReferrer = document.referrer
  if (documentReferrer?.length) {
    for (const [domain, source] of Object.entries(socialSourceMap)) {
      if (documentReferrer.includes(domain)) {
        return {
          utm_source: source,
          utm_medium: 'social',
        } as Partial<UtmParams>
      }
    }

    for (const [domain, source] of Object.entries(organicSourceMap)) {
      if (documentReferrer.includes(domain)) {
        return {
          utm_source: source,
          utm_medium: 'organic',
        } as Partial<UtmParams>
      }
    }

    return {
      utm_source: extractDomain(documentReferrer),
      utm_medium: 'referral',
    }
  }
}

const getUtmParamsFromReferrer = (): UtmParams | undefined => {
  const utmSourceAndMedium = getUtmSourceAndUtmMedium()
  if (!utmSourceAndMedium) {
    return
  }

  return {
    utm_campaign: 'not-provided',
    utm_term: 'not-provided',
    utm_content: 'not-provided',
    ...utmSourceAndMedium,
  } as UtmParams
}

export const setUtmParamsCookie = (): void => {
  const query = new URLSearchParams(window.location.search)
  const utmQueryParams: UtmParams = extractUtmParams(query)
  const utmParamsFromCookie = getUtmParamsFromCookie()
  let newUtmParams = Object.keys(utmQueryParams).length ? utmQueryParams : getUtmParamsFromReferrer()

  if (!newUtmParams && !utmParamsFromCookie) {
    newUtmParams = {
      utm_source: 'direct',
      utm_medium: 'direct',
    }
  }

  if (newUtmParams) {
    // New UTM params should overwrite old ones, except when overwriting external tracking with internal tracking
    const newSourceIsGrammarly = isGrammarlyDomain(newUtmParams?.utm_source)
    const storedSourceIsGrammarly = isGrammarlyDomain(utmParamsFromCookie?.utm_source)

    if (!newSourceIsGrammarly || storedSourceIsGrammarly) {
      setCookie(Cookie.utmParams, JSON.stringify(newUtmParams), 60 * 60 * 24)
    }
  }
}
